import { Component } from '@theme/component';

const SWIPE_THRESHOLD = 40;

/**
 * @typedef {Object} HeroCarouselRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement[]} content
 * @property {HTMLElement} panelsContainer
 * @property {HTMLElement} [announcer]
 */

/** @extends {Component<HeroCarouselRefs>} */
export class HeroCarouselComponent extends Component {
  requiredRefs = ['tabs', 'panels', 'content'];

  #active = 0;
  /** @type {number | undefined} */
  #interval;
  /** @type {AbortController | null} */
  #swipeAbort = null;
  /** @type {ResizeObserver | null} */
  #contentObserver = null;
  /** @type {AbortController | null} */
  #editorAbort = null;
  /** Theme editor: a slide's block is selected, so autoplay stays off. */
  #editorHold = false;

  connectedCallback() {
    super.connectedCallback();

    const { tabs, content, panelsContainer } = this.refs;
    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    if (panelsContainer) {
      panelsContainer.addEventListener('pointerdown', (event) => this.#startSwipe(event));
    }

    this.#contentObserver = new ResizeObserver(() => this.#syncContentHeight());
    for (const el of content) this.#contentObserver.observe(el);
    this.#syncContentHeight();
    this.#syncTabLabels();

    if (window.Shopify?.designMode) {
      this.#editorAbort = new AbortController();
      document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), {
        signal: this.#editorAbort.signal,
      });
      document.addEventListener(
        'shopify:block:deselect',
        () => {
          this.#editorHold = false;
        },
        { signal: this.#editorAbort.signal }
      );
    }

    // #activate() only (re)plays background video on an index *change*, so the
    // initially-active panel's video needs its own play() call here.
    this.refs.panels[this.#active]?.querySelector('video')?.play();

    if (this.autoplay && tabs.length > 1) this.play();
  }

  /**
   * Tab text mirrors each panel's `data-tab-label` (set server-side on the
   * panel's content element, where Liquid reliably resolves the block's
   * tab_label/title) instead of being computed a second time in the
   * tablist's own `{% for block in section.blocks %}` loop, which can end
   * up stale relative to the block-rendered content.
   */
  #syncTabLabels() {
    const { tabs, content } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      const label = content[i]?.dataset.tabLabel;
      if (!label) continue;

      const labelEl = tab.querySelector('.at-hero-carousel__tab-label');
      if (labelEl) {
        labelEl.textContent = label;
        labelEl.setAttribute('title', label);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.pause();
    this.#swipeAbort?.abort();
    this.#contentObserver?.disconnect();
    this.#editorAbort?.abort();
  }

  /**
   * Theme editor: show the slide whose block is selected in the sidebar, so
   * its title and CTAs are the ones visible while the merchant edits them.
   * @param {Event} event
   */
  #onBlockSelect(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const panel = event.target.closest('.at-hero-carousel__panel');
    if (!panel || !this.contains(panel)) return;

    const index = this.refs.panels.indexOf(/** @type {HTMLElement} */ (panel));
    if (index === -1) return;

    // Hold the merchant's slide while they edit it.
    this.#editorHold = true;
    this.#activate(index);
    this.pause();
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get autoplay() {
    return this.hasAttribute('autoplay') && !this.#reducedMotion;
  }

  get #autoplaySpeed() {
    const seconds = parseFloat(this.getAttribute('autoplay-speed') ?? '5');
    return (Number.isNaN(seconds) ? 5 : seconds) * 1000;
  }

  play() {
    if (this.#interval) return;

    this.removeAttribute('data-paused');
    this.#interval = window.setInterval(() => this.#advance(), this.#autoplaySpeed);
  }

  pause() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
    this.setAttribute('data-paused', '');
  }

  resume() {
    // Only a paused cycle restarts (a running one keeps its bar), and keyboard
    // focus in the tablist keeps it paused.
    if (!this.hasAttribute('data-paused') || this.querySelector(':focus-visible')) return;

    this.#restartAutoplay();
  }

  #advance() {
    const { tabs } = this.refs;
    this.#activate((this.#active + 1) % tabs.length);
  }

  /**
   * Declarative handler for `on:click="/select/{index}"` on each tab.
   * @param {number} index
   */
  select(index) {
    this.#activate(Number(index));

    // Keyboard users stay paused while focus is in the carousel (WCAG 2.2.2);
    // `resume()` restarts it when focus leaves.
    if (this.querySelector(':focus-visible')) {
      this.#resetProgress();
      this.pause();
      return;
    }

    this.#restartAutoplay();
  }

  /**
   * Restarts the countdown and the active tab's progress bar together, so the
   * bar always finishes exactly when the slide advances. Used after a pointer
   * pick (which plays even while the pointer is still over the hero, instead
   * of stopping autoplay) and when the pointer/focus leaves.
   */
  #restartAutoplay() {
    if (!this.autoplay || this.refs.tabs.length < 2 || this.#editorHold) return;

    window.clearInterval(this.#interval);
    this.#interval = undefined;
    this.#resetProgress();
    this.play();
  }

  #resetProgress() {
    const progress = this.refs.tabs[this.#active]?.querySelector('.at-hero-carousel__tab-progress');
    for (const animation of progress?.getAnimations({ subtree: true }) ?? []) {
      animation.currentTime = 0;
    }
  }

  /**
   * Declarative handler for `on:keydown="/handleKeydown"` on the tablist.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    const i = tabs.indexOf(target);
    if (i === -1) return;

    const offsets = { ArrowLeft: -1, ArrowRight: 1, Home: -i, End: tabs.length - 1 - i };
    const offset = offsets[/** @type {keyof typeof offsets} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (i + offset + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    this.select(nextIndex);
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs, panels, content, announcer } = this.refs;
    if (index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      const isActive = i === index;
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');

      const video = panel.querySelector('video');
      if (video) isActive ? video.play() : video.pause();
    }

    if (announcer) announcer.textContent = content[index]?.dataset.tabLabel ?? '';
    this.#syncContentHeight();
  }

  /**
   * Keeps the tablist pinned 16px above the active panel's content column,
   * whose height varies with the merchant's title/subtitle length.
   */
  #syncContentHeight() {
    const active = this.refs.content[this.#active];
    if (!active) return;

    this.style.setProperty('--at-hero-carousel-content-height', `${active.getBoundingClientRect().height}px`);
  }

  /**
   * @param {PointerEvent} event
   */
  #startSwipe(event) {
    if (event.pointerType === 'mouse') return;

    const startX = event.clientX;
    this.#swipeAbort?.abort();
    this.#swipeAbort = new AbortController();
    const { signal } = this.#swipeAbort;

    document.addEventListener(
      'pointermove',
      (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        if (Math.abs(delta) < SWIPE_THRESHOLD) return;

        const { tabs } = this.refs;
        const offset = delta < 0 ? 1 : -1;
        const nextIndex = (this.#active + offset + tabs.length) % tabs.length;
        this.select(nextIndex);
        this.#swipeAbort?.abort();
      },
      { signal }
    );

    document.addEventListener('pointerup', () => this.#swipeAbort?.abort(), { signal });
    document.addEventListener('pointercancel', () => this.#swipeAbort?.abort(), { signal });
  }
}

customElements.define('hero-carousel-component', HeroCarouselComponent);
